import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import LabTrends from "@/components/LabTrends";
import { FlaskConical, Calendar } from "lucide-react";

export default function PatientLabs() {
  const [labs, setLabs] = useState(null);
  const [tab, setTab] = useState("trends");
  useEffect(() => { api.get("/patient/labs").then((r) => setLabs(r.data)).catch(() => {}); }, []);
  if (!labs) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="patient-labs">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">My Labs</h1>
        <p className="text-sm text-muted-foreground mt-1">
          How your markers have moved, and every panel on file.
        </p>
      </div>

      <div className="flex gap-1 border-b border-border">
        {[["trends", "Over time"], ["panels", "By panel"]].map(([k, label]) => (
          <button key={k} data-testid={`labs-tab-${k}`} onClick={() => setTab(k)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    tab === k ? "border-primary text-foreground"
                              : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "trends" && <LabTrends path="/patient/lab-trends" />}

      {tab === "panels" && (labs.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-xl p-8 text-center text-sm text-muted-foreground">No labs on file yet.</div>
      ) : (
        <div className="space-y-3" data-testid="patient-labs-list">
          {labs.map((lab) => (
            <div key={lab.id} className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FlaskConical className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">{lab.panel_name}</span>
                </div>
                <StatusBadge status={lab.status} />
              </div>
              <p className="text-xs text-muted-foreground mt-1.5 inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {lab.due_date?.slice(0, 10)}
              </p>
              {lab.results?.length > 0 ? (
                <table className="w-full text-sm mt-4 border-t border-border pt-2">
                  <thead>
                    <tr className="text-xs text-muted-foreground">
                      <th className="text-left font-medium py-1.5">Marker</th>
                      <th className="text-right font-medium py-1.5">Result</th>
                      <th className="text-right font-medium py-1.5 hidden sm:table-cell">Reference</th>
                      <th className="text-right font-medium py-1.5">Flag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lab.results.map((r, i) => (
                      <tr key={i} className="border-t border-border/60">
                        <td className="py-1.5">{r.marker}</td>
                        <td className="py-1.5 text-right tabular-nums font-medium">{r.value} {r.unit}</td>
                        <td className="py-1.5 text-right tabular-nums text-muted-foreground hidden sm:table-cell">{r.ref_range}</td>
                        <td className="py-1.5 text-right"><StatusBadge status={r.flag} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-xs text-muted-foreground mt-3">Results will appear here once your provider has reviewed them.</p>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
